/**
 * A região viva que anuncia mensagem nova (H14, §11.1).
 *
 * ── Por que uma região à parte, e não a lista inteira ─────────────────────
 *
 * Marcar o histórico como região viva parece o caminho curto e é o errado: a
 * cada mudança o leitor de tela releria o que já foi lido, e quem depende dele
 * ouviria a conversa inteira de novo a cada mensagem. Numa conversa de trinta
 * mensagens, isso é a tela ficando inutilizável justamente para quem mais
 * precisa dela.
 *
 * Aqui a região é minúscula e existe só para isso: recebe uma frase por
 * mensagem nova, e nada mais.
 *
 * ── `polite`, nunca `assertive` ───────────────────────────────────────────
 *
 * A task pede *"mensagem nova anunciada sem interromper"*. `assertive` corta o
 * que estiver sendo lido — inclusive o que o atendente está digitando. Chegar
 * mensagem enquanto se escreve a resposta é rotina, e cortar a leitura nesse
 * momento é o oposto de ajudar.
 *
 * ── Só mensagem RECEBIDA é anunciada ──────────────────────────────────────
 *
 * A que o próprio atendente enviou ele já sabe que existe. Anunciá-la seria
 * ruído a cada envio, e ruído constante é o que faz alguém desligar o leitor de
 * tela — ou ignorar o anúncio que importa.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface MensagemAnunciavel {
  readonly referencia_externa: string;
  readonly direcao: 'entrada' | 'saida' | 'sistema';
  readonly conteudo: string | null;
  readonly autor: { readonly nome: string | null };
}

/**
 * Monta a frase do anúncio.
 *
 * Curta de propósito: quem ouve está no meio de outra coisa, e o conteúdo
 * inteiro está no histórico, que dá para percorrer. O nome vem primeiro porque
 * é o que decide se vale interromper o que se está fazendo.
 */
export function fraseDoAnuncio(mensagem: MensagemAnunciavel): string | null {
  if (mensagem.direcao !== 'entrada') return null;

  const quem = mensagem.autor.nome ?? 'o cliente';
  const texto = (mensagem.conteudo ?? '').trim();

  if (!texto) return `Nova mensagem de ${quem}.`;

  const trecho = texto.length > 120 ? `${texto.slice(0, 120)}…` : texto;
  return `Nova mensagem de ${quem}: ${trecho}`;
}

/**
 * Anuncia a última mensagem recebida, uma vez.
 *
 * A referência da última já anunciada fica numa `ref` para a mesma mensagem não
 * ser repetida a cada renderização — e são muitas: o cache invalida, a lista
 * rerenderiza, e sem a guarda o leitor repetiria a frase indefinidamente.
 */
export function AnuncioDeMensagem({
  mensagens,
}: {
  mensagens: readonly MensagemAnunciavel[];
}): ReactNode {
  const [frase, definirFrase] = useState('');
  const jaAnunciada = useRef<string | null>(null);
  const primeiraCarga = useRef(true);

  const ultima = [...mensagens].reverse().find((m) => m.direcao === 'entrada');

  useEffect(() => {
    if (!ultima) return;

    /**
     * A primeira carga NÃO anuncia.
     *
     * Abrir uma conversa lida ouviria "nova mensagem" para algo de três dias
     * atrás. O que interessa é o que chega COM a tela aberta.
     */
    if (primeiraCarga.current) {
      primeiraCarga.current = false;
      jaAnunciada.current = ultima.referencia_externa;
      return;
    }

    if (jaAnunciada.current === ultima.referencia_externa) return;

    jaAnunciada.current = ultima.referencia_externa;
    definirFrase(fraseDoAnuncio(ultima) ?? '');
  }, [ultima]);

  return (
    <p className="visualmente-oculto" role="status" aria-live="polite">
      {frase}
    </p>
  );
}
